import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import SignInDialog from "@/components/app/SignInDialog";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { JourneyProvider } from "@/context/JourneyContext";

import Navbar from "@/components/app/Navbar";
import Footer from "@/components/app/Footer";
import CookieBanner from "@/components/app/CookieBanner";
import CompareShortlistBar from "@/components/app/CompareShortlistBar";
import JourneyRail from "@/components/JourneyRail";
import ProgressAhaRibbon from "@/components/ProgressAhaRibbon";

import Landing from "@/pages/Landing";
import Explore from "@/pages/Explore";
import UniversityDetail from "@/pages/UniversityDetail";
import AdmissionCalculator from "@/pages/AdmissionCalculator";
import AdvancedSearch from "@/pages/AdvancedSearch";
import FlagshipsLanding from "@/pages/FlagshipsLanding";
import FlagshipPackDetail from "@/pages/FlagshipPackDetail";
import NordicTuitionReality from "@/pages/NordicTuitionReality";
import NordicFlagshipCorridor from "@/pages/NordicFlagshipCorridor";
import DegreeDetail from "@/pages/DegreeDetail";
import Compare from "@/pages/Compare";
import TrendingVerdicts from "@/pages/TrendingVerdicts";
import Shortlist from "@/pages/Shortlist";
import ShortlistParentReport from "@/pages/ShortlistParentReport";
import SharedParentReport from "@/pages/SharedParentReport";
import SavedSearches from "@/pages/SavedSearches";
import EmployerCompare from "@/pages/EmployerCompare";
import Specialists from "@/pages/Specialists";
import AuthCallback from "@/pages/AuthCallback";
import AuthMagicLink from "@/pages/AuthMagicLink";
import UkVisaFriendlyDegrees from "@/pages/UkVisaFriendlyDegrees";
import UsStemOptDegrees from "@/pages/UsStemOptDegrees";
import UsLiberalArtsColleges from "@/pages/UsLiberalArtsColleges";
import UkConservatoires from "@/pages/UkConservatoires";
import WuaPlusBook from "@/pages/WuaPlusBook";
import Plus from "@/pages/Plus";
import PlusMyReports from "@/pages/PlusMyReports";
import CrossCountryCompare from "@/pages/CrossCountryCompare";
import SubjectRankings from "@/pages/SubjectRankings";
import AdminGradUrlHealth from "@/pages/AdminGradUrlHealth";
import GradeCalculator from "@/pages/GradeCalculator";
import Survey from "@/pages/Survey";
import AdminReviews from "@/pages/AdminReviews";
import AdminSurveyInvites from "@/pages/AdminSurveyInvites";
import AdminVerify from "@/pages/AdminVerify";
import AdminVerifyDetail from "@/pages/AdminVerifyDetail";
import AdminEditorialCalendar from "@/pages/AdminEditorialCalendar";
import AdminOpsFacultyPhotos from "@/pages/AdminOpsFacultyPhotos";
import AdminOpsCampusVideos from "@/pages/AdminOpsCampusVideos";
import AdminSalaryCoverage from "@/pages/AdminSalaryCoverage";
import MethodologySources from "@/pages/MethodologySources";
import UkCoverage from "@/pages/UkCoverage";
import AuCoverage from "@/pages/AuCoverage";
import CoverageHub from "@/pages/CoverageHub";
import BetaCourseFit from "@/pages/BetaCourseFit";
import FastMatchAudit from "@/pages/FastMatchAudit";
import TalentProfile from "@/pages/TalentProfile";
import StudentProfilePage from "@/pages/StudentProfilePage";
import MyRealisations from "@/pages/MyRealisations";
import SciSubjectPreview from "@/pages/sci/SciSubjectPreview";
import SciIntlPreview from "@/pages/sci/SciIntlPreview";
import SciStudentGuide from "@/pages/sci/SciStudentGuide";
import SubjectsDirectory from "@/pages/SubjectsDirectory";
import OAuthAuthorize from "@/pages/OAuthAuthorize";
import DataIntegrityPreview from "@/pages/admin/DataIntegrityPreview";
import SciCoursePreview from "@/pages/sci/SciCoursePreview";
import ConsentsPage from "@/pages/ConsentsPage";
import ApplicationDetailPage from "@/pages/ApplicationDetailPage";
import ApplicationsOverviewPage from "@/pages/ApplicationsOverviewPage";
import DeadlinesBrowse from "@/pages/DeadlinesBrowse";
import JourneyMap from "@/pages/JourneyMap";
import DataSources from "@/pages/DataSources";
import MethodologyFeeStatus from "@/pages/MethodologyFeeStatus";
import ValueCountriesPage from "@/pages/ValueCountriesPage";
import AdminHome from "@/pages/AdminHome";
import AdminProgressDash from "@/pages/AdminProgressDash";
import AdminCourseFitGovernance from "@/pages/AdminCourseFitGovernance";
import AdminClaims from "@/pages/AdminClaims";
import AdminImpersonate from "@/pages/AdminImpersonate";
import AdminSurveys from "@/pages/AdminSurveys";
import AdminDataCoverage from "@/pages/AdminDataCoverage";
import AdminDataGapReports from "@/pages/AdminDataGapReports";
import AdminUniVerification from "@/pages/AdminUniVerification";
import AdminEditBrowser from "@/pages/AdminEditBrowser";
import { AdminEditDegreePage, AdminEditUniversityPage } from "@/pages/AdminEditPage";
import AdminAIFetchBatch from "@/pages/AdminAIFetchBatch";
import AdminEditorialMatches from "@/pages/AdminEditorialMatches";
import UniClaim from "@/pages/UniClaim";
import UniTrust from "@/pages/UniTrust";
import UniPortal from "@/pages/UniPortal";
import UniPortalEdit from "@/pages/UniPortalEdit";
import UniPortalDegreeEdit from "@/pages/UniPortalDegreeEdit";
import Cities from "@/pages/Cities";
import CityDetail from "@/pages/CityDetail";
import CompareCities from "@/pages/CompareCities";
import CityMatcher from "@/pages/CityMatcher";
import CityQuizPage from "@/pages/CityQuizPage";
import CityHubsIndex from "@/pages/CityHubsIndex";
import CityHubPage from "@/pages/CityHubPage";
import HighestEarningCourses from "@/pages/HighestEarningCourses";
import EmployerDetail from "@/pages/EmployerDetail";
import EmployersIndex from "@/pages/EmployersIndex";
import AdminUcasQueue from "@/pages/AdminUcasQueue";
import AdminCoverageHealth from "@/pages/AdminCoverageHealth";
import AdminCoverageBenchmarks from "@/pages/AdminCoverageBenchmarks";
import AdminEntryTariffQueue from "@/pages/AdminEntryTariffQueue";
import AdminCourseReview from "@/pages/AdminCourseReview";
import AdminFeaturedCities from "@/pages/AdminFeaturedCities";
import Legal from "@/pages/Legal";
import AdminLeads from "@/pages/AdminLeads";
import AdminFirmQueues from "@/pages/AdminFirmQueues";
import FirmClaim from "@/pages/FirmClaim";
import FirmPortal, { FirmPortalIndex } from "@/pages/FirmPortal";
import EventsHub from "@/pages/EventsHub";
import CountryLanding from "@/pages/CountryLanding";
import UkFeColleges from "@/pages/UkFeColleges";
import FeCollegeCompare from "@/pages/FeCollegeCompare";
import UniCompare from "@/pages/UniCompare";
import FoundationYearLanding from "@/pages/FoundationYearLanding";
import MatureLearnerLanding from "@/pages/MatureLearnerLanding";
import PathwaysHub from "@/pages/PathwaysHub";
import About from "@/pages/About";
import Methodology from "@/pages/Methodology";
import Guides from "@/pages/Guides";
import MethodologyCorrections from "@/pages/MethodologyCorrections";
import CompareShortResolver from "@/pages/CompareShortResolver";
import HowToGetIntoFirm from "@/pages/HowToGetIntoFirm";
import WuaPlusSuccess from "@/pages/WuaPlusSuccess";
import WuaPlusCancel from "@/pages/WuaPlusCancel";
import WuaPlusReport from "@/pages/WuaPlusReport";
import WuaPlusSample from "@/pages/WuaPlusSample";
import JournalGoldmanFeeders from "@/pages/JournalGoldmanFeeders";
import FirmGuidesIndex from "@/pages/FirmGuidesIndex";
import GetMeTo from "@/pages/GetMeTo";
import Clearing from "@/pages/Clearing";
import ApplyPrimersIndex from "@/pages/ApplyPrimersIndex";
import ApplyPrimer from "@/pages/ApplyPrimer";
import CounsellorsLanding from "@/pages/CounsellorsLanding";
import CounsellorsDashboard from "@/pages/CounsellorsDashboard";
import CohortOutlookPrint from "@/pages/CohortOutlookPrint";
import CounsellorsTopUpSuccess from "@/pages/CounsellorsTopUpSuccess";
import StudentShare from "@/pages/StudentShare";
import JourneyPublic from "@/pages/JourneyPublic";

// Scroll the window to the top on every route change so the new page
// always opens at the header, matching typical page-load expectations.
// If a URL hash is present, smooth-scroll to that element instead so
// deep-links like `/#city-quiz` still land students where they intend.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait a tick so the target has been rendered post-navigation.
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (el) el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
        else window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }, 120);
      return () => clearTimeout(t);
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: detect OAuth session_id in URL fragment DURING render, before any /auth/me check
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <JourneyRail />
      <ProgressAhaRibbon />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/uni/:id" element={<UniversityDetail />} />
          <Route path="/degree/:id" element={<DegreeDetail />} />
          <Route path="/admission" element={<AdmissionCalculator />} />
          <Route path="/grade-calculator" element={<GradeCalculator />} />
          <Route path="/advanced" element={<AdvancedSearch />} />
          <Route path="/flagships" element={<FlagshipsLanding />} />
          <Route path="/flagships/:slug" element={<FlagshipPackDetail />} />
          <Route path="/nordic-tuition-reality" element={<NordicTuitionReality />} />
          <Route path="/nordic-flagship-corridor" element={<NordicFlagshipCorridor />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/rankings" element={<SubjectRankings />} />
          <Route path="/rankings/:countrySlug" element={<SubjectRankings />} />
          <Route path="/rankings/:countrySlug/:subjectSlug" element={<SubjectRankings />} />
          <Route path="/rankings/:countrySlug/:subjectSlug/:cohortSlug" element={<SubjectRankings />} />
          <Route path="/admin/grad-url-health" element={<AdminGradUrlHealth />} />
          <Route path="/compare/trending" element={<TrendingVerdicts />} />
          <Route path="/specialists" element={<Specialists />} />
          <Route path="/shortlist" element={<Shortlist />} />
          <Route path="/shortlist/parent-report" element={<ShortlistParentReport />} />
          <Route path="/shared/parent-report/:slug" element={<SharedParentReport />} />
          <Route path="/clearing" element={<Clearing />} />
          <Route path="/apply" element={<ApplyPrimersIndex />} />
          <Route path="/apply/:country" element={<ApplyPrimer />} />
          <Route path="/counsellors" element={<CounsellorsLanding />} />
          <Route path="/counsellors/dashboard" element={<CounsellorsDashboard />} />
          <Route path="/counsellors/cohorts/:cohortId/outlook" element={<CohortOutlookPrint />} />
          {/* Shareable school-invite link — colleagues open this and
              land on the dashboard with the join form pre-filled. */}
          <Route path="/counsellors/join" element={<CounsellorsDashboard />} />
          <Route path="/counsellors/topup/success" element={<CounsellorsTopUpSuccess />} />
          <Route path="/s/:token" element={<StudentShare />} />
          <Route path="/journey/:ticketId" element={<JourneyPublic />} />
          <Route path="/uk-visa-friendly-degrees" element={<UkVisaFriendlyDegrees />} />
          <Route path="/us-stem-opt-degrees" element={<UsStemOptDegrees />} />
          <Route path="/us-liberal-arts-colleges" element={<UsLiberalArtsColleges />} />
          <Route path="/uk-conservatoires" element={<UkConservatoires />} />
          <Route path="/highest-earning" element={<HighestEarningCourses />} />
          <Route path="/employers" element={<EmployersIndex />} />
          <Route path="/employers/compare" element={<EmployerCompare />} />
          <Route path="/employers/:slug" element={<EmployerDetail />} />
          <Route path="/how-to-get-into/:slug" element={<HowToGetIntoFirm />} />
          <Route path="/get-me-to/:slug" element={<GetMeTo />} />
          <Route path="/firm-guides" element={<FirmGuidesIndex />} />
          <Route path="/saved" element={<SavedSearches />} />
          <Route path="/wua-plus" element={<WuaPlusBook />} />
          <Route path="/wua-plus/success" element={<WuaPlusSuccess />} />
          <Route path="/wua-plus/cancel" element={<WuaPlusCancel />} />
          <Route path="/wua-plus/report/:id" element={<WuaPlusReport />} />
          <Route path="/wua-plus/sample" element={<WuaPlusSample />} />
          <Route path="/journal/goldman-feeder-unis-2026" element={<JournalGoldmanFeeders />} />
          <Route path="/plus" element={<Plus />} />
          <Route path="/plus/my-reports" element={<PlusMyReports />} />
          <Route path="/explore/cross-country" element={<CrossCountryCompare />} />
          <Route path="/countries/:code" element={<CountryLanding />} />
          <Route path="/uk-fe-colleges" element={<UkFeColleges />} />
          <Route path="/uk-fe-colleges/compare" element={<FeCollegeCompare />} />
          <Route path="/compare/unis" element={<UniCompare />} />
          <Route path="/pathways" element={<PathwaysHub />} />
          <Route path="/about" element={<About />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/methodology" element={<Methodology />} />
          <Route path="/methodology/corrections" element={<MethodologyCorrections />} />
          <Route path="/pathways/foundation-year" element={<FoundationYearLanding />} />
          <Route path="/pathways/mature-learner" element={<MatureLearnerLanding />} />
          <Route path="/c/:code" element={<CompareShortResolver />} />
          <Route path="/survey/:degreeId" element={<Survey />} />
          <Route path="/admin" element={<AdminHome />} />
          <Route path="/admin/progress" element={<AdminProgressDash />} />
          <Route path="/admin/course-fit/governance" element={<AdminCourseFitGovernance />} />
          <Route path="/admin/course-fit/governance" element={<AdminCourseFitGovernance />} />
          <Route path="/admin/reviews" element={<AdminReviews />} />
          <Route path="/admin/editorial-matches" element={<AdminEditorialMatches />} />
          <Route path="/admin/surveys/invites" element={<AdminSurveyInvites />} />
          <Route path="/admin/verify" element={<AdminVerify />} />
          <Route path="/admin/verify/:id" element={<AdminVerifyDetail />} />
          <Route path="/admin/calendar" element={<AdminEditorialCalendar />} />
          <Route path="/admin/ops/faculty-photos" element={<AdminOpsFacultyPhotos />} />
          <Route path="/admin/ops/campus-videos" element={<AdminOpsCampusVideos />} />
          <Route path="/admin/salary-coverage" element={<AdminSalaryCoverage />} />
          <Route path="/methodology/sources" element={<MethodologySources />} />
          <Route path="/coverage" element={<CoverageHub />} />
          <Route path="/coverage/uk" element={<UkCoverage />} />
          <Route path="/coverage/au" element={<AuCoverage />} />
          <Route path="/coverage/au" element={<AuCoverage />} />
          <Route path="/methodology/data-sources" element={<DataSources />} />
          <Route path="/methodology/fee-status" element={<MethodologyFeeStatus />} />
          <Route path="/value/countries" element={<ValueCountriesPage />} />
          <Route path="/admin/claims" element={<AdminClaims />} />
          <Route path="/admin/impersonate" element={<AdminImpersonate />} />
          <Route path="/admin/surveys" element={<AdminSurveys />} />
          <Route path="/admin/data-coverage" element={<AdminDataCoverage />} />
          <Route path="/admin/data-gap-reports" element={<AdminDataGapReports />} />
          <Route path="/admin/uni-verification" element={<AdminUniVerification />} />
          <Route path="/admin/edit" element={<AdminEditBrowser />} />
          <Route path="/admin/edit/degree/:id" element={<AdminEditDegreePage />} />
          <Route path="/admin/edit/university/:id" element={<AdminEditUniversityPage />} />
          <Route path="/admin/ai-fetch-batch" element={<AdminAIFetchBatch />} />
          <Route path="/admin/ucas" element={<AdminUcasQueue />} />
          <Route path="/admin/coverage-health" element={<AdminCoverageHealth />} />
          <Route path="/admin/coverage-benchmarks" element={<AdminCoverageBenchmarks />} />
          <Route path="/admin/entry-tariff" element={<AdminEntryTariffQueue />} />
          <Route path="/admin/course-review" element={<AdminCourseReview />} />
          <Route path="/admin/featured-cities" element={<AdminFeaturedCities />} />
          <Route path="/admin/leads" element={<AdminLeads />} />
          <Route path="/admin/firm-portal" element={<AdminFirmQueues />} />
          <Route path="/claim/:slug" element={<FirmClaim />} />
          <Route path="/firm-portal" element={<FirmPortalIndex />} />
          <Route path="/firm-portal/:slug" element={<FirmPortal />} />
          <Route path="/events" element={<EventsHub />} />
          <Route path="/uni/:id/claim" element={<UniClaim />} />
          <Route path="/uni/:id/trust" element={<UniTrust />} />
          <Route path="/uni-portal" element={<UniPortal />} />
          <Route path="/uni-portal/uni/:id" element={<UniPortalEdit />} />
          <Route path="/uni-portal/degree/:id" element={<UniPortalDegreeEdit />} />
          <Route path="/cities" element={<Cities />} />
          <Route path="/cities/matcher" element={<CityMatcher />} />
          <Route path="/city-quiz" element={<CityQuizPage />} />
          <Route path="/cities/hubs" element={<CityHubsIndex />} />
          <Route path="/cities/hubs/:tag" element={<CityHubPage />} />
          <Route path="/cities/compare" element={<CompareCities />} />
          <Route path="/cities/:slug" element={<CityDetail />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/magic-link" element={<AuthMagicLink />} />
          <Route path="/legal/:slug" element={<Legal />} />
          <Route path="/beta/course-fit" element={<BetaCourseFit />} />
          <Route path="/subjects" element={<SubjectsDirectory />} />
          <Route path="/subjects/:slug" element={<SciStudentGuide publicMode />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
          <Route path="/fast-match/audit" element={<FastMatchAudit />} />
          <Route path="/me/talent-profile" element={<TalentProfile />} />
          <Route path="/profile" element={<StudentProfilePage />} />
          <Route path="/realisations" element={<MyRealisations />} />
          <Route path="/preview/sci/subjects/:slug/international" element={<SciIntlPreview />} />
          <Route path="/preview/sci/subjects/:slug/student" element={<SciStudentGuide />} />
          <Route path="/preview/data-integrity" element={<DataIntegrityPreview />} />
          <Route path="/preview/sci/subjects/:slug" element={<SciSubjectPreview />} />
          <Route path="/preview/sci/courses/:degreeId" element={<SciCoursePreview />} />
          <Route path="/profile/consents" element={<ConsentsPage />} />
          <Route path="/applications" element={<ApplicationsOverviewPage />} />
          <Route path="/applications/:degreeId" element={<ApplicationDetailPage />} />
          <Route path="/deadlines" element={<DeadlinesBrowse />} />
          <Route path="/journey" element={<JourneyMap />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
      <CompareShortlistBar />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <JourneyProvider>
              <AppRouter />
              <SignInDialog />
              <Toaster richColors closeButton position="top-right" />
            </JourneyProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
